import { useQuery } from 'react-query';

import type { Holiday, Jurisdiction } from '../../../../backend/src/matter/procedural-timeline/date-resolution/index';
import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig from '@/api/useGetFetchConfig';

interface UseProceduralHolidaysInput {
  matterId: string | undefined;
  jurisdiction: Jurisdiction;
  fromYear: number;
  toYear: number;
}

export type ProceduralHoliday = Omit<Holiday, 'date'> & { date: string };

export const useProceduralHolidays = ({ matterId, jurisdiction, fromYear, toYear }: UseProceduralHolidaysInput) => {
  const { fetchConfigGET } = useGetFetchConfig();

  return useQuery<Holiday[], Error>(
    [QueryEntity.ProceduralTimelineHolidays, { matterId, jurisdiction, fromYear, toYear }],
    async () => {
      const params = new URLSearchParams({
        jurisdiction,
        fromYear: String(fromYear),
        toYear: String(toYear),
      });
      const res = await fetch(
        `${APIBaseChronos}/api/matter/${matterId}/procedural-timeline/holidays?${params.toString()}`,
        fetchConfigGET,
      );
      if (!res.ok) throw new Error('Failed to fetch public holiday data.');
      const raw = (await res.json()) as ProceduralHoliday[];
      return raw.map((h) => ({ ...h, date: new Date(h.date) }));
    },
    {
      enabled: matterId !== undefined && Number.isFinite(fromYear) && Number.isFinite(toYear) && toYear >= fromYear,
      refetchOnWindowFocus: false,
      staleTime: 60 * 60 * 1000,
    },
  );
};
